import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Text } from '@mantine/core'

import {
  CalendarCourtReporterAdminEvent,
  CourtReporter,
  EventFormat,
} from '@/apiClient'
import {
  CourtReporterFilter,
  CourtReporterStatusFilter,
  EventFormatFilter,
  SearchFilter,
  StateFilter,
  TimezoneFilter,
} from '@/components/Calendar/FilterControls'
import { useCourtReportersQuery } from '@/queries/courtReporters'

export type CourtReporterFilters = {
  general_status_in: CalendarCourtReporterAdminEvent['general_status'][]
  court_reporter?: CourtReporter
  event_format?: EventFormat
  event_search?: string
  team_search?: string
  organization_search?: string
  state_in?: string[]
  timezone_in?: string[]
}

export const CourtReporterFilterControls: React.FC<{
  values: CourtReporterFilters
  onChange: (filters: CourtReporterFilters) => void
}> = ({ values: initialFilters, onChange }) => {
  const [filters, setFilters] = useState<CourtReporterFilters>(initialFilters)
  const { data: allCourtReporters = [] } = useCourtReportersQuery()

  const courtReporters = useMemo(
    () => allCourtReporters.filter(cr => cr.is_active),
    [allCourtReporters],
  )

  useEffect(() => {
    onChange(filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  return (
    <Box ml="xl" mr="xl" mt="xs">
      <Stack>
        <Text weight="bold">Visible events</Text>
        <Stack spacing="sm">
          <CourtReporterStatusFilter
            values={filters.general_status_in}
            onChange={statuses =>
              setFilters(filters => ({
                ...filters,
                general_status_in: statuses,
              }))
            }
          />
        </Stack>
        <Text weight="bold" mt="sm">
          Filters
        </Text>
        <Stack spacing="md">
          <SearchFilter
            value={filters.event_search ?? ''}
            placeholder="Search name"
            onChange={event_search =>
              setFilters(filters => ({ ...filters, event_search }))
            }
          />
          <SearchFilter
            value={filters.team_search ?? ''}
            placeholder="Search team"
            onChange={team_search =>
              setFilters(filters => ({ ...filters, team_search }))
            }
          />
          <SearchFilter
            placeholder="Search organization"
            value={filters.organization_search ?? ''}
            onChange={organization_search =>
              setFilters(filters => ({ ...filters, organization_search }))
            }
          />
          <CourtReporterFilter
            courtReporters={courtReporters}
            value={filters.court_reporter}
            onChange={court_reporter =>
              setFilters(filters => ({ ...filters, court_reporter }))
            }
          />
          <EventFormatFilter
            value={filters.event_format}
            onChange={event_format =>
              setFilters(filters => ({ ...filters, event_format }))
            }
          />
          <StateFilter
            value={filters.state_in}
            onChange={state_in =>
              setFilters(filters => ({ ...filters, state_in }))
            }
          />
          <TimezoneFilter
            value={filters.timezone_in}
            onChange={timezone_in =>
              setFilters(filters => ({ ...filters, timezone_in }))
            }
          />
        </Stack>
      </Stack>
    </Box>
  )
}
